import * as React from "react";
import { cn } from "@/lib/utils";

interface SectionHeadingProps {
  /** Small uppercase label above the headline. */
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: "center" | "left";
  className?: string;
}

/**
 * One shared anatomy for every marketing section: eyebrow -> headline -> body.
 * Keeps rhythm and type scale identical across the whole landing experience.
 */
export const SectionHeading = ({
  eyebrow,
  title,
  description,
  align = "center",
  className,
}: SectionHeadingProps) => (
  <div
    className={cn(
      "mb-14 sm:mb-16 max-w-3xl",
      align === "center" ? "mx-auto text-center" : "text-left",
      className
    )}
  >
    {eyebrow && (
      <div
        className={cn(
          "mb-4 inline-flex items-center gap-2 rounded-full border border-subtle bg-card/60 px-3 py-1.5 backdrop-blur-sm",
          align === "center" ? "mx-auto" : ""
        )}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight text-foreground text-balance">
      {title}
    </h2>
    {description && (
      <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
        {description}
      </p>
    )}
  </div>
);
